import { Image, Text, View } from '@react-pdf/renderer';

import { CompTypeBadge } from '../components/CompTypeBadge.tsx';
import SalesComparisonRow from '../components/SalesComparisonRow.tsx';
import { Tkv2PDFPage } from '../components/Tkv2PDFPage.tsx';
import { salesComparisonColumns } from '../constants.ts';
import { salesComparisonPageStyles as styles } from '../styles.ts';
import { SalesComparisonGridColumnData, SalesComparisonPageProps } from '../types.ts';

export const SalesComparisonPage = ({
  subjectAddress,
  subjectSalesComparisonData,
  subjectArvSalesComparisonData,
  comparables,
  hasArvOption,
}: SalesComparisonPageProps) => {
  // Group comparables into chunks of 3 per page
  const compsPerPage = 3;
  const pages: SalesComparisonGridColumnData[][] = [];
  const aivPages: SalesComparisonGridColumnData[][] = [];
  const arvPages: SalesComparisonGridColumnData[][] = [];
  const noTypeComps = comparables.filter((comp) => !comp.compType);
  const aivComps = comparables.filter((comp) => comp.compType === 'aiv');
  const arvComps = comparables.filter((comp) => comp.compType === 'arv');
  for (let i = 0; i < noTypeComps.length; i += compsPerPage) {
    pages.push(noTypeComps.slice(i, i + compsPerPage));
  }

  for (let i = 0; i < aivComps.length; i += compsPerPage) {
    aivPages.push(aivComps.slice(i, i + compsPerPage));
  }

  for (let i = 0; i < arvComps.length; i += compsPerPage) {
    arvPages.push(arvComps.slice(i, i + compsPerPage));
  }

  if (!hasArvOption) {
    return (
      <>
        {pages.map((chunk, pageIndex) => (
          <Tkv2PDFPage
            key={`sales-comparison-page-${pageIndex}`}
            title="Sales Comparison"
            address={subjectAddress}
          >
            <View style={styles.content}>
              <View style={styles.row}>
                {[subjectSalesComparisonData, ...chunk].map((comp, i) => (
                  <View style={styles.gridColumn} key={`scc-card-${comp?.address}`}>
                    <View>
                      <Text style={styles.header}>
                        {i === 0 ? 'SUBJECT' : `COMP ${comp?.position}`}
                      </Text>
                    </View>
                    <View style={styles.imageContainer}>
                      {comp?.imageUrl && <Image src={comp.imageUrl} style={styles.image} />}
                    </View>
                  </View>
                ))}
              </View>

              <View style={styles.row}>
                {[subjectSalesComparisonData, ...chunk].map((comp) => (
                  <View
                    style={{ ...styles.gridColumn, ...styles.addressContainer }}
                    key={`scc-address-${comp?.address}`}
                  >
                    <Text style={styles.address}>{comp?.address}</Text>
                  </View>
                ))}
              </View>

              {salesComparisonColumns.map((field) => (
                <SalesComparisonRow
                  key={`scc-field-${field.key[0]}`}
                  fields={field.key}
                  labels={field.label}
                  subject={subjectSalesComparisonData as SalesComparisonGridColumnData}
                  comparables={chunk}
                  adjustment={field.adjustment as keyof SalesComparisonGridColumnData}
                  borderTop={field.borderTop}
                  bold={field.bold}
                  align={(field.align as 'left' | 'right' | 'center') || 'left'}
                />
              ))}
            </View>
          </Tkv2PDFPage>
        ))}
      </>
    );
  }

  return (
    <>
      {aivPages.map((chunk, pageIndex) => (
        <Tkv2PDFPage
          key={`sales-comparison-page-${pageIndex}`}
          title="Sales Comparison"
          address={subjectAddress}
        >
          <View style={styles.content}>
            <View style={styles.row}>
              {[subjectSalesComparisonData, ...chunk].map((comp, i) => (
                <View style={styles.gridColumn} key={`scc-card-${comp?.address}`}>
                  <View style={[styles.row, styles.rowSpaceBetween]}>
                    <Text style={styles.header}>
                      {i === 0 ? 'SUBJECT' : `COMP ${comp?.position}`}
                    </Text>
                    <CompTypeBadge compType="aiv" fullText compact />
                  </View>
                  <View style={styles.imageContainer}>
                    {comp?.imageUrl && <Image src={comp.imageUrl} style={styles.image} />}
                  </View>
                </View>
              ))}
            </View>

            <View style={styles.row}>
              {[subjectSalesComparisonData, ...chunk].map((comp) => (
                <View
                  style={{ ...styles.gridColumn, ...styles.addressContainer }}
                  key={`scc-address-${comp?.address}`}
                >
                  <Text style={styles.address}>{comp?.address}</Text>
                </View>
              ))}
            </View>

            {salesComparisonColumns.map((field) => (
              <SalesComparisonRow
                key={`scc-field-${field.key[0]}`}
                fields={field.key}
                labels={field.label}
                subject={subjectSalesComparisonData as SalesComparisonGridColumnData}
                comparables={chunk}
                adjustment={field.adjustment as keyof SalesComparisonGridColumnData}
                borderTop={field.borderTop}
                bold={field.bold}
                align={(field.align as 'left' | 'right' | 'center') || 'left'}
              />
            ))}
          </View>
        </Tkv2PDFPage>
      ))}
      {arvPages.map((chunk, pageIndex) => (
        <Tkv2PDFPage
          key={`sales-comparison-page-${pageIndex}`}
          title="Sales Comparison"
          address={subjectAddress}
        >
          <View style={styles.content}>
            <View style={styles.row}>
              {[subjectArvSalesComparisonData, ...chunk].map((comp, i) => (
                <View style={styles.gridColumn} key={`scc-card-${comp?.address}`}>
                  <View style={[styles.row, styles.rowSpaceBetween]}>
                    <Text style={styles.header}>
                      {i === 0 ? 'SUBJECT' : `COMP ${comp?.position}`}
                    </Text>
                    <CompTypeBadge compType="arv" fullText compact />
                  </View>
                  <View style={styles.imageContainer}>
                    {comp?.imageUrl && <Image src={comp.imageUrl} style={styles.image} />}
                  </View>
                </View>
              ))}
            </View>

            <View style={styles.row}>
              {[subjectArvSalesComparisonData, ...chunk].map((comp) => (
                <View
                  style={{ ...styles.gridColumn, ...styles.addressContainer }}
                  key={`scc-address-${comp?.address}`}
                >
                  <Text style={styles.address}>{comp?.address}</Text>
                </View>
              ))}
            </View>

            {salesComparisonColumns.map((field) => (
              <SalesComparisonRow
                key={`scc-field-${field.key[0]}`}
                fields={field.key}
                labels={field.label}
                subject={subjectArvSalesComparisonData as SalesComparisonGridColumnData}
                comparables={chunk}
                adjustment={field.adjustment as keyof SalesComparisonGridColumnData}
                borderTop={field.borderTop}
                bold={field.bold}
                align={(field.align as 'left' | 'right' | 'center') || 'left'}
              />
            ))}
          </View>
        </Tkv2PDFPage>
      ))}
    </>
  );
};
